import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckIcon,
  TableCellsIcon,
  ShoppingCartIcon,
  FireIcon,
  ChartBarIcon,
  DevicePhoneMobileIcon,
  CloudIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

const features = [
  {
    icon: TableCellsIcon,
    title: 'Gestion de Sala',
    description:
      'Plano interactivo de mesas con estados en tiempo real. Reservas, asignaciones y seguimiento de ocupacion.',
  },
  {
    icon: ShoppingCartIcon,
    title: 'Comandas Inteligentes',
    description:
      'Toma de pedidos rapida con asignacion por comensal, modificadores y notas especiales.',
  },
  {
    icon: FireIcon,
    title: 'Cocina (KDS)',
    description:
      'Pantallas de cocina por partida. Coordinacion de pases y tiempos de preparacion.',
  },
  {
    icon: ChartBarIcon,
    title: 'Business Intelligence',
    description:
      'Dashboards en tiempo real, reportes de ventas, rentabilidad por plato y analisis de tendencias.',
  },
  {
    icon: DevicePhoneMobileIcon,
    title: 'Multi-dispositivo',
    description:
      'Funciona en tablets, moviles y ordenadores. Interfaz adaptativa para cada situacion.',
  },
  {
    icon: CloudIcon,
    title: 'Offline First',
    description:
      'Sigue funcionando sin conexion. Los datos se sincronizan automaticamente al recuperar internet.',
  },
];

const plans = [
  {
    name: 'Basic',
    price: 49,
    description: 'Para restaurantes pequenos',
    features: [
      'Hasta 10 mesas',
      '5 usuarios',
      '1 pantalla KDS',
      'Soporte por email',
      'Actualizaciones incluidas',
    ],
    highlighted: false,
  },
  {
    name: 'Pro',
    price: 99,
    description: 'Para restaurantes medianos',
    features: [
      'Hasta 50 mesas',
      '20 usuarios',
      '5 pantallas KDS',
      'Reservas online',
      'CRM avanzado',
      'Soporte prioritario',
    ],
    highlighted: true,
  },
  {
    name: 'Enterprise',
    price: 199,
    description: 'Para grupos y franquicias',
    features: [
      'Mesas ilimitadas',
      'Usuarios ilimitados',
      'KDS ilimitado',
      'API de integracion',
      'Multi-restaurante',
      'Soporte 24/7',
    ],
    highlighted: false,
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-950/80 backdrop-blur-lg border-b border-dark-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">TPV</span>
              </div>
              <span className="font-semibold text-white">TPV Restaurante</span>
            </div>

            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-dark-300 hover:text-white transition-colors">
                Caracteristicas
              </a>
              <a href="#pricing" className="text-dark-300 hover:text-white transition-colors">
                Precios
              </a>
              <a href="#contact" className="text-dark-300 hover:text-white transition-colors">
                Contacto
              </a>
            </div>

            <div className="flex items-center gap-4">
              <a
                href="https://demo-restaurant.restaurantes.sisfarma.info/login"
                className="text-dark-300 hover:text-white transition-colors"
              >
                Iniciar Sesion
              </a>
              <Link to="/registro" className="btn-primary">
                Empezar Gratis
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 hero-gradient overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight"
            >
              El TPV que tu restaurante{' '}
              <span className="gradient-text">merece</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mt-6 text-xl text-dark-300 max-w-2xl mx-auto"
            >
              Sistema de gestion integral para restaurantes de alta gastronomia.
              Sala, comandas, cocina, inventario y mas. Todo en la nube.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link to="/registro" className="btn-primary text-lg px-8 py-4">
                Prueba Gratis 14 Dias
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </Link>
              <a href="#demo" className="btn-secondary text-lg px-8 py-4">
                Ver Demo
              </a>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-4 text-sm text-dark-500"
            >
              Sin tarjeta de credito. Sin compromiso.
            </motion.p>
          </div>

          {/* Hero image placeholder */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-16 relative"
          >
            <div className="aspect-video bg-dark-900 rounded-2xl border border-dark-800 shadow-2xl overflow-hidden">
              <div className="w-full h-full bg-gradient-to-br from-dark-800 to-dark-900 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 bg-primary-600/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <TableCellsIcon className="w-10 h-10 text-primary-500" />
                  </div>
                  <p className="text-dark-400">Vista previa del dashboard</p>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-3/4 h-8 bg-primary-600/20 blur-2xl" />
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold">
              Todo lo que necesitas para gestionar tu restaurante
            </h2>
            <p className="mt-4 text-xl text-dark-400 max-w-2xl mx-auto">
              Herramientas profesionales disenadas para la alta gastronomia
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card p-6 hover:border-primary-500/50 transition-colors"
              >
                <div className="w-12 h-12 bg-primary-600/20 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-dark-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Disenado para restaurantes de alta gastronomia
              </h2>
              <div className="space-y-4">
                {[
                  'Gestion de alergenos integrada (14 alergenos UE)',
                  'Control de pases y tiempos de cocina',
                  'Escandallos y control de costes',
                  'Bodega y carta de vinos',
                  'CRM con historial de clientes',
                  'Modo offline para eventos',
                ].map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckIcon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-dark-200">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="card p-8"
            >
              <div className="aspect-square bg-dark-800 rounded-xl flex items-center justify-center">
                <ShieldCheckIcon className="w-24 h-24 text-primary-500/50" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold">
              Planes adaptados a tu negocio
            </h2>
            <p className="mt-4 text-xl text-dark-400">
              Sin permanencia. Cambia o cancela cuando quieras.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`card p-8 ${
                  plan.highlighted
                    ? 'border-primary-500 ring-2 ring-primary-500/20'
                    : ''
                }`}
              >
                {plan.highlighted && (
                  <div className="text-center mb-4">
                    <span className="px-3 py-1 bg-primary-600 text-white text-sm font-medium rounded-full">
                      Mas Popular
                    </span>
                  </div>
                )}

                <h3 className="text-2xl font-bold text-center">{plan.name}</h3>
                <p className="text-dark-400 text-center mt-1">{plan.description}</p>

                <div className="text-center my-8">
                  <span className="text-5xl font-bold">{plan.price}€</span>
                  <span className="text-dark-400">/mes</span>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map(feature => (
                    <li key={feature} className="flex items-center gap-2">
                      <CheckIcon className="w-5 h-5 text-primary-500 flex-shrink-0" />
                      <span className="text-dark-300">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/registro?plan=${plan.name.toLowerCase()}`}
                  className={`w-full ${plan.highlighted ? 'btn-primary' : 'btn-secondary'}`}
                >
                  Empezar Ahora
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Empieza a optimizar tu restaurante hoy
          </h2>
          <p className="text-xl text-dark-400 mb-8">
            Prueba gratis durante 14 dias. Sin tarjeta de credito.
          </p>
          <Link to="/registro" className="btn-primary text-lg px-8 py-4">
            Crear Cuenta Gratis
            <ArrowRightIcon className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-dark-900 border-t border-dark-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">TPV</span>
                </div>
                <span className="font-semibold text-white">TPV Restaurante</span>
              </div>
              <p className="text-dark-400 text-sm">
                Sistema de gestion integral para restaurantes de alta gastronomia.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Producto</h4>
              <ul className="space-y-2 text-dark-400 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Caracteristicas</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Precios</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Integraciones</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Actualizaciones</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Soporte</h4>
              <ul className="space-y-2 text-dark-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Centro de Ayuda</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Documentacion</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contacto</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Estado del Sistema</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-dark-400 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Privacidad</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terminos</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Cookies</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-dark-800 mt-12 pt-8 text-center text-dark-500 text-sm">
            <p>&copy; {new Date().getFullYear()} TPV Restaurante. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
